import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  QueryList,
  ViewChild,
  inject,
} from '@angular/core';
import { FilterMatchMode, FilterMetadata, SortEvent } from 'primeng/api';
import { FileUploadModule } from 'primeng/fileupload';
import { Menu } from 'primeng/menu';
import { Table, TableModule } from 'primeng/table';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { deepCompare, SeahorseTemplateDirective } from '@seahorse/common';
import { CustomDataSearchFieldModel, FieldType } from '@seahorse/domain';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { PagedList } from './models/paged-list.model';
import { FILTER_OPERATORS } from './models/table-filter.model';
import { TableRequest } from './models/table-request.model';
import {
  ColumnModel,
  DEFAULT_PAGE_SIZES,
  RowActionEvent,
  TableDataLoadEvent,
  TableModel,
  columnFilterTypes,
} from './models/table.model';
import { TableAdapter } from './services/table.adapter';
import { EmptyTableTemplate } from './templates/empty-table.template';
import {
  CopyContentWrapperComponent,
  FieldValuePipe,
  ProgressBarComponent,
} from '@seahorse/ui';
import { FormComponent } from '../form/form.component';
import { InlineFormActionsTemplate } from '../form/inline-form-actions.template';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'temp-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableModule,
    CommonModule,
    FormsModule,
    CheckboxModule,
    FileUploadModule,
    MenuModule,
    EmptyTableTemplate,
    TranslateModule,
    ButtonModule,
    TooltipModule,
    MessageModule,
    CopyContentWrapperComponent,
    FieldValuePipe,
    FormComponent,
    InlineFormActionsTemplate,
    ProgressBarComponent,
  ],
  providers: [TableAdapter],
})
export class SeahorseTableComponent {
  @ViewChild('rowActions', { read: Menu }) rowActionsRef?: Menu;

  private _tableRef?: Table;

  @ViewChild(Table) set tableRef(val: Table | undefined) {
    this._tableRef = val;

    if (!val) {
      return;
    }

    this.setUpFilterIndicators();
  }

  @ContentChildren(SeahorseTemplateDirective)
  templates?: QueryList<SeahorseTemplateDirective>;

  @HostBinding('class.has-footer')
  get footerTemplate() {
    return this.templates?.find((x) => x.type === 'footer')?.templateRef;
  }

  get cellTemplate() {
    return this.templates?.find((x) => x.type === 'cell')?.templateRef;
  }

  get rowActionsTemplate() {
    return this.templates?.find((x) => x.type === 'rowActions')?.templateRef;
  }

  private _model?: TableModel<any>;
  @Input() set model(val: TableModel<any> | undefined) {
    if (!val) {
      return;
    }

    this._model = undefined;

    // force template re-evaluation
    setTimeout(() => {
      this._model = val;
      this._tableAdapter.adapt(this);
      this._cdRef.markForCheck();
    });
  }

  get model() {
    return this._model;
  }

  get visibleColumns() {
    return this.model?.columns.filter((column) => !column.hidden) || [];
  }

  private _data = PagedList.empty();
  @Input() set data(val: PagedList<any>) {
    this._data = val;
    this._tableAdapter.adapt(this);
  }

  get data() {
    return this._data;
  }

  @HostBinding('class.show-header')
  get showHeader() {
    if (!this.model) {
      return false;
    }

    return (
      ![undefined].includes(this.model.selectionMode) ||
      this.model.enableFiltering
    );
  }

  private _isLoading = false;

  @Input() set isLoading(val: boolean) {
    this._isLoading = val;

    if (val) {
      this.clearSelection();
      this.clearCheckedItems();
    }
  }
  get isLoading() {
    return this._isLoading;
  }

  get rows() {
    return (
      (!this.model?.serverSide || !this.data?.totalCount
        ? this.model?.pageSize
        : this.data.pageSize > this.data.totalCount
        ? this.data.totalCount
        : this.data.pageSize) || 10
    );
  }

  @Input() totalRecords = 0;

  @Output() loadData = new EventEmitter<TableDataLoadEvent>();
  @Output() rowSelect = new EventEmitter<any>();
  @Output() rowDblClick = new EventEmitter<any>();
  @Output() rowAction = new EventEmitter<RowActionEvent<any>>();
  @Output() checked = new EventEmitter<any[]>();
  @Output() submitRow = new EventEmitter<any>();
  @Output() cancelRow = new EventEmitter<any>();
  @Output() rowReorder = new EventEmitter<{ ids: string[]; items: any[] }>();

  private _loadDataSub = new Subject<TableDataLoadEvent>();
  private _loadDataSub$ = this._loadDataSub.asObservable();

  selectedRow?: any;
  selectedRows: any[] = [];

  FieldType = FieldType;
  columnFilterTypes = columnFilterTypes;
  FilterMatchMode = FilterMatchMode;

  rowValidation: { [key: string]: boolean } = {};
  activeFilters: { [key: string]: boolean } = {};
  expandedRowKeys: Record<string, boolean> = {};
  groupByColumn?: ColumnModel;
  isValid = true;
  isMutating = false;

  get isClickable() {
    return (
      this.rowSelect.observers.length > 0 ||
      this.rowDblClick.observers.length > 0
    );
  }

  get isMultipleSelection() {
    return this.model?.selectionMode === 'multiple';
  }

  get hasFilters() {
    if (!this._tableRef) {
      return false;
    }

    const filters = Object.values(
      this._tableRef?.filters
    ) as FilterMetadata[][];

    return filters
      .map((filters) => filters.some((filter) => filter.value != null))
      .some((hasFilter) => hasFilter);
  }

  get totalCollspan() {
    return (
      (this.model?.columns.length ?? 0) +
      (this.model?.rowActions ? 1 : 0) +
      (this.isMultipleSelection ? 1 : 0) +
      (this.model?.supportsReordering ? 1 : 0)
    );
  }

  get groupRowsBy() {
    return this.model?.groupBy ? '__group' : null;
  }

  get dataKey() {
    return this.model?.id ?? 'id';
  }

  get rowsPerPageOptions() {
    return this.model?.rowsPerPage ?? DEFAULT_PAGE_SIZES;
  }

  private _cdRef = inject(ChangeDetectorRef);
  private _translateService = inject(TranslateService);
  private _tableAdapter = inject(TableAdapter);

  constructor() {
    this._loadDataSub$.pipe(debounceTime(300)).subscribe((event) => {
      this.loadData.emit(event);
    });
  }

  trackByColumn = (index: number, item: ColumnModel) => {
    return item.key;
  };

  customSort(event: SortEvent) {
    if (!this.model?.sortBy?.sortFn) {
      return;
    }

    if (!event.data) {
      return;
    }

    const sortFn = this.model.sortBy.sortFn as (a: any, b: any) => number;

    event.data.sort(sortFn);
  }

  isRowReadonly(row: any) {
    return (
      this.model.form == null ||
      (this.model.form &&
        this.model.editModeTrigger === 'checkbox' &&
        this.selectedRows.indexOf(row) === -1) ||
      (this.model.form &&
        this.model.editModeTrigger === 'click' &&
        this.selectedRow !== row)
    );
  }

  submitInlineForm(originalValue: any, newValue: any) {
    const areThereChanges = !deepCompare(originalValue, newValue);

    if (!areThereChanges) {
      this.cancelInlineForm(originalValue);
      return;
    }

    this.submitRow.emit(newValue);
  }

  cancelInlineForm(originalValue: any) {
    this.cancelRow.emit(originalValue);
  }

  onCloseInlineForm(originalValue: any, newValue: any) {
    const areThereChanges = !deepCompare(originalValue, newValue);

    if (areThereChanges) {
      this.submitRow.emit(newValue);
    }
  }

  setUpFilterIndicators() {
    this._tableRef?.onFilter.subscribe((event: any) => {
      if (event.filters) {
        this.setActiveFilters(event.filters);
      }
    });
  }

  setActiveFilters(filters: any) {
    if (!filters) {
      return;
    }

    this.activeFilters = Object.keys(filters).reduce((acc, key) => {
      const metadata = filters[key] as FilterMetadata[];
      acc[key] = metadata.some((filter) => filter.value != null);
      return acc;
    }, {} as { [key: string]: boolean });
  }

  detectChanges() {
    this._cdRef.detectChanges();
  }

  resetFilters() {
    if (!this._tableRef) {
      return;
    }

    this._tableRef.reset();
    this.activeFilters = {};
  }

  showRowActions(row: any, event: Event) {
    if (!this.rowActionsRef) {
      return;
    }

    const actions = this.model?.rowActions?.map((rowAction) => {
      const isDisabled =
        rowAction.condition && rowAction.condition(row) === false;
      const action = {
        ...rowAction,
      };

      if (!isDisabled) {
        action.command = () => this._actionCallback(row, rowAction.key);
      } else {
        action.disabled = true;
      }

      action.label = this._translateService.instant(action.label ?? '');

      return action;
    });

    this.rowActionsRef.model = actions ?? [];
    this.rowActionsRef.toggle(event);
  }

  private _actionCallback = (data: any, action: string) => {
    this.rowAction.emit({ data, action });
  };

  onLoadData(event: any) {
    const dataLoadEvent: TableDataLoadEvent = {
      ...event,
    };

    let pageIndex = 0;

    if (event.first !== null && event.rows !== null && event.rows > 0) {
      pageIndex = event.first / event.rows;
    }

    dataLoadEvent.pageIndex = pageIndex;
    dataLoadEvent.pageSize = event.rows;

    const filters = event.filters as {
      [s: string]: FilterMetadata[] | undefined;
    };

    dataLoadEvent.filters = filters
      ? Object.keys(filters)
          .filter(
            (key) =>
              filters[key] &&
              filters[key]?.length &&
              filters[key]?.[0]?.value != null &&
              filters[key]?.[0]?.matchMode
          )
          .map((key) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const filter = filters[key]![0];
            const matchMode = filter.matchMode as keyof typeof FILTER_OPERATORS;

            return {
              fieldName: key,
              searchValue: filter.value,
              searchOperator: FILTER_OPERATORS[matchMode],
            } as CustomDataSearchFieldModel;
          })
      : [];

    this._loadDataSub.next(dataLoadEvent);
  }

  onRowSelect(row: any) {
    if (!this.isClickable) {
      return;
    }

    this.selectedRow = row;
    this.rowSelect.emit(row);
  }

  onRowsChecked(rows: any[]) {
    this.selectedRows = rows;
    this.checked.emit(this.selectedRows);
  }

  onRowDblClick(row: any) {
    if (!this.isClickable) {
      return;
    }

    this.rowDblClick.emit(row);
  }

  trackByFn = (index: number, item: any) => {
    const key = this.model?.id;

    if (key) {
      return item[key];
    }

    return index;
  };

  clearSelection() {
    this.selectedRow = undefined;
    this._cdRef.markForCheck(); // it can be called outside
  }

  clearCheckedItems() {
    this.onRowsChecked([]);
    this._cdRef.markForCheck(); // it can be called outside
  }

  setFilters(filters: TableRequest['filters']) {
    if (!this._tableRef || !filters) {
      return;
    }

    this._tableRef.filters = filters as Record<
      string,
      FilterMetadata | FilterMetadata[]
    >;
  }

  onPageChange() {
    const isServerSideGrouping =
      this.model?.serverSide && !!this.model?.groupBy;

    if (isServerSideGrouping) {
      const lazyLoadEvent = this._tableRef?.createLazyLoadMetadata();

      if (lazyLoadEvent) {
        this.onLoadData(lazyLoadEvent);
      }
    }
  }

  onRowReorder(event: any) {
    const items = (this.data.items ?? []).map((item: any, idx: number) => {
      item.__SortOrder = idx + 1;
      return item;
    });

    this._data = {
      ...this._data,
      items,
    };

    this._cdRef.markForCheck();

    this.rowReorder.emit({
      ids: items.map((x) => x.__Id ?? x.id).filter(Boolean),
      items,
    });
  }

  mutateModelAndData(model: TableModel<any>, data: PagedList<any>) {
    this.isMutating = true;

    this.model = model;
    this.data = data;

    setTimeout(() => {
      this.isMutating = false;
      this._cdRef.detectChanges();
    });
  }

  isRowChecked(row: unknown): boolean {
    if (!this.isMultipleSelection) {
      return false;
    }
    return this.selectedRows.some(
      (x) => x[this.model?.id ?? 'id'] === row[this.model?.id ?? 'id']
    );
  }

  getGroupItems(groupKey: string): any[] {
    return this.data.items.filter((item) => item['__group'] === groupKey);
  }

  isGroupChecked(group: any): boolean {
    const items = this.getGroupItems(group['__group']);
    return items.length > 0 && items.every((item) => this.isRowChecked(item));
  }

  isGroupIndeterminate(group: any): boolean {
    const items = this.getGroupItems(group['__group']);
    const checkedCount = items.filter((item) => this.isRowChecked(item)).length;
    return checkedCount > 0 && checkedCount < items.length;
  }

  toggleGroupSelection(group: any): void {
    const idKey = this.model?.id ?? 'id';
    const items = this.getGroupItems(group['__group']);
    const allChecked = this.isGroupChecked(group);

    if (allChecked) {
      const groupIds = new Set(items.map((item) => item[idKey]));
      const newSelection = this.selectedRows.filter(
        (row) => !groupIds.has(row[idKey])
      );
      this.onRowsChecked(newSelection);
    } else {
      const selectedIds = new Set(this.selectedRows.map((row) => row[idKey]));
      const toAdd = items.filter((item) => !selectedIds.has(item[idKey]));
      this.onRowsChecked([...this.selectedRows, ...toAdd]);
    }
  }
}
